/**
 * V2.0.79 — User-drawn walls SVG render layer extracted from
 * FloorPlan2D.jsx (originally added in V2.0.77 for the click-to-draw
 * wall tool). Renders walls in `floor.walls[]` that carry the
 * `user_drawn: true` flag as thick dark strokes on top of the
 * room-derived wall segments, plus the dashed preview during an
 * active draw session.
 *
 * The parent still owns the wall-tool state (via `useWallDrawTool`
 * hook) and the coordinate transforms (`toX`, `toY`, `scale`). This
 * component is purely presentational.
 */
import React from "react";

export default function UserWallsLayer({
  floor,
  toX,
  toY,
  scale,
  wallTool,
}) {
  const walls = (floor?.walls || []).filter(
    (w) => w && w.user_drawn && !w.deleted,
  );

  return (
    <>
      {walls.map((w) => {
        const s = w.start || [w.x1 ?? 0, w.y1 ?? 0];
        const e = w.end || [w.x2 ?? 0, w.y2 ?? 0];
        const x1 = toX(s[0]);
        const y1 = toY(s[1]);
        const x2 = toX(e[0]);
        const y2 = toY(e[1]);
        return (
          <g key={`user-wall-${w.id}`} data-testid={`user-wall-${w.id}`}>
            <line
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke="#1f2937"
              strokeWidth={Math.max(3, (w.thickness || 0.12) * scale)}
              strokeLinecap="round"
              style={{ cursor: wallTool.active ? "crosshair" : "pointer" }}
              onClick={(ev) => wallTool.deleteWall(ev, w.id)}
            >
              <title>Click to delete this wall</title>
            </line>
          </g>
        );
      })}

      {/* Dashed preview between the last committed start and the cursor. */}
      {wallTool.active && wallTool.draft && wallTool.preview && (
        <g pointerEvents="none" data-testid="wall-draw-preview">
          <line
            x1={toX(wallTool.draft.start.x_m)}
            y1={toY(wallTool.draft.start.z_m)}
            x2={toX(wallTool.preview.x_m)}
            y2={toY(wallTool.preview.z_m)}
            stroke="#b45309"
            strokeWidth={3}
            strokeDasharray="6 4"
            strokeLinecap="round"
            opacity={0.9}
          />
          <circle
            cx={toX(wallTool.draft.start.x_m)}
            cy={toY(wallTool.draft.start.z_m)}
            r={4}
            fill="#b45309"
          />
          <circle
            cx={toX(wallTool.preview.x_m)}
            cy={toY(wallTool.preview.z_m)}
            r={4}
            fill="#fbbf24"
            stroke="#b45309"
            strokeWidth={1.5}
          />
        </g>
      )}
    </>
  );
}
